import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Trophy } from "@phosphor-icons/react";
import { adminRevenueApi } from "@/lib/api";
import { money, shortDate } from "@/lib/format";

const LABELS = { razorpay: "Razorpay", stripe: "Stripe", monthly: "Monthly", yearly: "Yearly", pro: "Pro", team: "Team", sale: "Sale price", full: "Full price", unknown: "Not recorded" };

function Split({ title, data, testId }) {
  const entries = Object.entries(data || {});
  const total = entries.reduce((a, [, v]) => a + v.count, 0) || 1;
  return (
    <div className="stp-card rounded-3xl p-5" data-testid={testId}>
      <p className="text-xs font-medium uppercase tracking-wider text-mist">{title}</p>
      {entries.length === 0 ? <p className="mt-3 text-sm text-mist">No sales yet</p> : entries.map(([k, v]) => (
        <div key={k} className="mt-3">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-ink">{LABELS[k] || k}</span>
            <span className="text-mist">{v.count} · {[v.inr ? money(v.inr, "inr") : null, v.usd ? money(v.usd, "usd") : null].filter(Boolean).join(" + ") || "—"}</span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-black/5"><div className="h-full rounded-full bg-brand" style={{ width: `${(v.count / total) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="stp-card rounded-3xl p-5">
      <p className="text-xs font-medium uppercase tracking-wider text-mist">{label}</p>
      <p data-testid={testId} className="mt-2 font-display text-3xl font-semibold text-ink">{value}</p>
    </div>
  );
}

function Leaderboard({ rows }) {
  const thank = async (r) => { await navigator.clipboard.writeText(r.email); toast.success(`Copied ${r.email} so you can thank them`); };
  return (
    <div className="stp-card overflow-x-auto rounded-3xl p-5 sm:p-6" data-testid="invite-leaderboard">
      <p className="mb-3 flex items-center gap-2 font-display text-lg font-semibold text-ink"><Trophy size={18} weight="fill" className="text-[#FF9F0A]" /> Top inviters</p>
      {rows.length === 0 ? <p className="text-sm text-mist">No invites yet</p> : (
        <table className="w-full min-w-[520px] text-sm">
          <thead><tr className="text-left text-xs text-mist"><th className="pb-2 font-medium">#</th><th className="pb-2 font-medium">Inviter</th><th className="pb-2 font-medium">Signed up</th><th className="pb-2 font-medium">Paid</th><th className="pb-2 font-medium">Weeks earned</th><th /></tr></thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r, i) => (
              <tr key={r.user_id} data-testid="leaderboard-row">
                <td className="py-2.5 font-semibold text-ink">{i + 1}</td>
                <td className="py-2.5"><span className="block text-ink">{r.email}</span><span className="text-[11px] text-mist">{r.name}</span></td>
                <td className="py-2.5 text-ink">{r.signups}</td>
                <td className="py-2.5 font-semibold text-[#0B6B2A]">{r.paid}</td>
                <td className="py-2.5 text-ink">{r.weeks}/12</td>
                <td className="py-2.5 text-right"><button data-testid="leaderboard-thank-button" onClick={() => thank(r)} className="rounded-full bg-black/5 px-3 py-1 text-xs font-medium text-ink hover:bg-black/10">Copy email</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function RevenuePanel() {
  const [d, setD] = useState(null);
  const [cur, setCur] = useState("inr");
  useEffect(() => { adminRevenueApi().then(setD).catch(() => {}); }, []);
  if (!d) return <p className="text-sm text-mist">Loading revenue…</p>;

  return (
    <div className="space-y-5" data-testid="revenue-panel">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Revenue in ₹" value={money(d.total.inr, "inr")} testId="revenue-total-inr" />
        <Stat label="Revenue in $" value={money(d.total.usd, "usd")} testId="revenue-total-usd" />
        <Stat label="Payments" value={d.total.count} testId="revenue-count" />
        <Stat label="Invites → paid" value={`${d.referrals.signups} → ${d.referrals.paid}`} testId="revenue-referrals" />
      </div>
      <div className="stp-card rounded-3xl p-5 sm:p-6" data-testid="revenue-chart">
        <div className="mb-4 flex items-center justify-between">
          <p className="font-display text-lg font-semibold text-ink">Last 30 days</p>
          <div className="inline-flex rounded-full bg-black/5 p-1">
            {[["inr", "₹"], ["usd", "$"]].map(([id, l]) => (
              <button key={id} data-testid={`revenue-chart-${id}`} onClick={() => setCur(id)} className={`rounded-full px-3 py-1 text-xs font-medium ${cur === id ? "bg-white text-ink shadow-sm" : "text-mist"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={d.daily} margin={{ left: 0, right: 8, top: 4 }}>
              <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0071E3" stopOpacity={0.3} /><stop offset="100%" stopColor="#0071E3" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="rgba(0,0,0,0.05)" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} tick={{ fontSize: 11, fill: "#6E6E73" }} axisLine={false} tickLine={false} interval={4} />
              <YAxis tick={{ fontSize: 11, fill: "#6E6E73" }} axisLine={false} tickLine={false} width={50} />
              <Tooltip formatter={(v) => money(v, cur)} labelFormatter={(l) => shortDate(l)} contentStyle={{ borderRadius: 12, border: "1px solid rgba(0,0,0,0.06)" }} />
              <Area type="monotone" dataKey={cur} name="Revenue" stroke="#0071E3" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Split title="Payment route" data={d.by_provider} testId="revenue-by-provider" />
        <Split title="Billing" data={d.by_interval} testId="revenue-by-interval" />
        <Split title="Plan" data={d.by_plan} testId="revenue-by-plan" />
        <Split title="Pricing" data={d.by_pricing} testId="revenue-by-pricing" />
      </div>
      <Leaderboard rows={d.leaderboard || []} />
      <div className="stp-card overflow-x-auto rounded-3xl p-5 sm:p-6" data-testid="revenue-recent">
        <p className="mb-3 font-display text-lg font-semibold text-ink">Recent payments</p>
        {d.recent.length === 0 ? <p className="text-sm text-mist">No payments yet</p> : (
          <table className="w-full min-w-[560px] text-sm">
            <thead><tr className="text-left text-xs text-mist"><th className="pb-2 font-medium">Date</th><th className="pb-2 font-medium">Customer</th><th className="pb-2 font-medium">Plan</th><th className="pb-2 font-medium">Route</th><th className="pb-2 text-right font-medium">Amount</th></tr></thead>
            <tbody className="divide-y divide-black/5">
              {d.recent.map((r, i) => (
                <tr key={i} data-testid="revenue-row">
                  <td className="py-2.5 text-mist">{shortDate(r.date)}</td>
                  <td className="py-2.5 text-ink">{r.email}</td>
                  <td className="py-2.5 text-ink">{LABELS[r.plan]} · {LABELS[r.interval] || r.interval}</td>
                  <td className="py-2.5 text-mist">{LABELS[r.provider]}{r.sale_code ? ` · ${r.sale_code}` : ""}</td>
                  <td className="py-2.5 text-right font-semibold text-ink">{money(r.amount, r.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
